import React, { useState, useCallback, useRef } from 'react';
import { getVendorTransactions } from '../../services/api';
import { useSocket } from '../../context/SocketContext';
import { useApiQuery } from '../../hooks/useApiQuery';
import { format } from 'date-fns';
import useToast from '../../hooks/useToast';

const VendorTransactions = () => {
  const [page, setPage] = useState(1);
  const limit = 50;
  const { showError } = useToast();
  const socket = useSocket();
  const isInitialLoad = useRef(true);

  // ✅ React Query: Fetch vendor transactions with pagination
  const { 
    data, 
    isLoading, 
    isFetching,
    refetch 
  } = useApiQuery(
    ['vendor-transactions', page, limit],
    () => getVendorTransactions({ page, limit }).then(res => res.data),
    {
      staleTime: 30 * 1000, // 30 seconds
      keepPreviousData: true, // ✅ Keep old data while fetching new page
      onError: (error) => {
        console.error('Failed to fetch transactions:', error);
        showError('Failed to load transactions');
      },
    }
  );

  // ✅ Extract data from response
  const transactions = data?.transactions || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // ✅ Socket listener for real-time updates
  React.useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      console.log('🔄 New transaction detected, refreshing...');
      // ✅ Go back to page 1 to see newest transaction
      setPage(1);
      // ✅ Refetch data
      refetch();
    };

    socket.on('walletUpdated', handleUpdate);
    return () => socket.off('walletUpdated', handleUpdate);
  }, [socket, refetch]);

  // ✅ Handle page change with keepPreviousData
  const handlePreviousPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  // ✅ Show loading only on initial load
  if (isLoading && transactions.length === 0) {
    return (
      <div className="text-center py-10 text-[#708090]">
        <div className="inline-block w-8 h-8 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
        <p>Loading transactions...</p>
      </div>
    );
  }

  // ✅ Format date safely
  const formatDate = (transaction) => {
    if (transaction.createdAt) {
      return format(new Date(transaction.createdAt), 'dd MMM yyyy, HH:mm');
    } else if (transaction._id) {
      const timestamp = parseInt(transaction._id.toString().slice(0, 8), 16) * 1000;
      return format(new Date(timestamp), 'dd MMM yyyy, HH:mm');
    }
    return '-';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-normal text-[#2b3445]">Transaction History</h1>
        {isFetching && (
          <span className="text-sm text-[#708090] animate-pulse">
            Refreshing...
          </span>
        )}
      </div>

      <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-5">
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-[#708090]">No transactions yet.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#dae1e7]">
                <thead className="bg-[#f8f9fa]">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-[#708090] uppercase">Attendee</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-[#708090] uppercase">Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-[#708090] uppercase">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-[#708090] uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#dae1e7]">
                  {transactions.map((t) => (
                    <tr key={t._id}>
                      <td className="px-3 py-2 text-sm text-[#2b3445]">
                        {t.userId?.name || t.userId}
                      </td>
                      <td className="px-3 py-2 text-sm font-semibold text-green-600">
                        +₹{t.amount.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-sm text-[#708090]">
                        {t.description || '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-[#708090]">
                        {formatDate(t)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#dae1e7]">
              <button
                onClick={handlePreviousPage}
                disabled={page === 1 || isFetching}
                className="px-4 py-2 bg-[#1471b9] text-white rounded-[8px] text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-2 text-sm text-[#708090]">
                <span>Page</span>
                <span className="font-medium text-[#2b3445]">{page}</span>
                <span>of</span>
                <span className="font-medium text-[#2b3445]">{totalPages || 1}</span>
              </div>

              <button
                onClick={handleNextPage}
                disabled={page >= totalPages || isFetching}
                className="px-4 py-2 bg-[#1471b9] text-white rounded-[8px] text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VendorTransactions;